import { Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule, MatSelectTrigger } from '@angular/material/select';
import { IRole } from '@core/interfaces/role.interface';
import { PermissionService } from '@core/services/permission.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ERoles } from '@enums/roles.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { translateRoleName } from '@utilities/role-name.util';
import { roleExceedsPermissions } from '@utilities/role-permission-comparison.util';
import { filter } from 'rxjs';
import { UserManagementUsersTabStore } from '../user-management-users-tab.store';
import { ICampaignSubUserInvitationDialogData } from './campaign-sub-user-invitation.interface';

/**
 * "Invite user" dialog - uses the same `app-side-dialog` shell as every other dialog in this
 * feature area (Select Role/Team, Add Team, Add Custom Role). No name field: the backend's
 * invitation request has no name field (see CampaignSubUserInvitationRequest), so nothing is
 * collected for it.
 */
@Component({
  imports: [
    DotsSpinnerComponent,
    IconComponent,
    MatButtonModule,
    MatErrorComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSelectTrigger,
    ReactiveFormsModule,
    SideDialogComponent,
    TranslateModule,
  ],
  providers: [UserManagementUsersTabStore],
  selector: 'app-campaign-sub-user-invitation',
  templateUrl: './campaign-sub-user-invitation.component.html',
})
export class CampaignSubUserInvitationComponent {
  public readonly form = new FormGroup({
    email: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    message: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    role: new FormControl<string>(ERoles.campaignSub, { nonNullable: true, validators: [Validators.required] }),
    team: new FormControl<string | null>(null),
  });

  public readonly data = inject<ICampaignSubUserInvitationDialogData>(MAT_DIALOG_DATA);
  public readonly store = inject(UserManagementUsersTabStore);
  private readonly dialogRef = inject(MatDialogRef<CampaignSubUserInvitationComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);

  /** The currently logged-in (inviting) user's own permissions - not the invitee's. Reactive so a
   * permission change elsewhere in the session is reflected immediately - see
   * SelectRoleDialogComponent's identical field for the same reasoning. */
  private readonly currentUserPermissions = toSignal(this.permissionService.currentUserPermissions$, {
    initialValue: this.permissionService.currentUserPermissions,
  });

  /** Only roles that don't grant the invitee more than the inviting user already holds - mirrors
   * SelectRoleDialogComponent's isRoleAssignable, applied here as a straight filter (rather than a
   * disabled-with-tooltip option) since this is a plain `<mat-select>`, not that dialog's custom
   * button list. Without this, anyone holding just `campaignSubUserInvitationCreate` could invite
   * a brand-new user directly into a role broader than their own (e.g. Admin). */
  public readonly assignableRoles = computed<IRole[]>(() =>
    this.data.roles.filter((role) => !roleExceedsPermissions(role, this.currentUserPermissions())),
  );

  constructor() {
    // No explicit error handling here on `store.sendInvitationError$()` - the global ApiInterceptor already
    // shows a snackbar for the failed request (with the actual backend message, where there is
    // one), and `SnackbarService` only ever shows one snackbar at a time, so a second, generic
    // one here would just immediately replace it with a less useful "unknown error" message.
    // `sendInvitation`'s own `catchError` (see UserManagementUsersTabStore) already resets
    // `loading` back to `false` on failure, so there's nothing left for this component to do.
    effect(() => {
      if (this.store.userInvitation$()) {
        this.snackbarService.openSuccess(this.translate.instant('pages.settings.userManagement.users.invitationSent'));
        this.dialogRef.close(this.store.userInvitation$()!);
        this.store.patchState({ sendInvitationLoading: false });
      }
    });
  }

  public getRoleName(role: IRole): string {
    return translateRoleName(role, this.translate);
  }

  public getSelectedTeamName(): string | null {
    const teamId = this.form.controls.team.value;

    return teamId ? (this.data.teams.find((team) => team.id === teamId)?.name ?? null) : null;
  }

  public onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    const formValue = this.form.getRawValue();

    // Save-time guard alongside the dropdown's own filtering (assignableRoles) - defends against
    // the selected role's permissions, or the inviting user's own, changing between render and
    // submit (e.g. a role edited by someone else in another tab during this session).
    if (!this.assignableRoles().some((role) => role.id === formValue.role)) {
      this.form.controls.role.setErrors({ roleNotAssignable: true });
      this.form.controls.role.markAsTouched();

      return;
    }

    this.store.sendInvitation({
      email: formValue.email,
      message: formValue.message,
      roleId: formValue.role,
      teamId: formValue.team,
    });
  }

  public onCloseClick(): void {
    if (this.store.sendInvitationLoading$()) {
      return;
    }

    // `form.dirty` alone is a correct "has changed" signal here (unlike e.g.
    // AddCustomRoleDialogComponent) - every field on this form is a real ReactiveForms control,
    // so there's no extra non-form-control state (like a permissions-toggle set) to fold in.
    // `role`'s non-empty initial value doesn't spuriously mark this dirty - Angular only flags a
    // control dirty on actual user interaction, not on its initial value.
    if (!this.form.dirty) {
      this.dialogRef.close(undefined);

      return;
    }

    const confirmRef = this.dialog.open<ConfirmDialogComponent, IConfirmDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.cancel'),
        confirmText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.confirm'),
        message: this.translate.instant('pages.settings.userManagement.discardChangesDialog.message'),
        swapButtons: true,
        title: this.translate.instant('pages.settings.userManagement.discardChangesDialog.title'),
      } as IConfirmDialogData,
      width: '400px',
    });

    confirmRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => this.dialogRef.close(undefined));
  }
}
